/**
 * TrafficManager.ts — Runs the level's traffic and ends the run on a hit.
 *
 * A vehicle drives along a road in a fixed direction using UK left-hand lanes
 * (systems/roads.ts) and is despawned once it leaves the map. One touching the
 * player OR the dog fires `onHit` exactly once — the game core turns that into
 * a game over.
 *
 * ## The horn
 *
 * A vehicle bearing down on the walker or the dog sounds off first (`onHorn`).
 * The camera shows a few seconds of road either side of the walker (see
 * `CAMERA.DISTANCE`) but much less behind them, so a car coming up from down
 * screen is inside two seconds away the moment it appears — and without the horn
 * the only warning of the one thing that ends a run outright is the car itself.
 * The test for "bearing down" — in range *and* in front — is the
 * pure `systems/horn.ts`; the cooldown is per vehicle, so a queue of them can
 * each get a word in but none of them drones.
 *
 * ## Where the cars come from
 *
 * The level's `traffic.schedule` (see `levelgen/types.ts` → `VehicleRelease`):
 * a written-down list of which road, which way, and when. That is what lets the
 * level generator *prove* a street is crossable before anyone plays it — you
 * cannot check a random timer, and traffic is the only thing in the game that
 * ends a run outright.
 *
 * The schedule loops every `loopMs` so a slow player still meets traffic, and
 * each release is nudged by a little jitter on the way out. The jitter is
 * cosmetic and deliberately smaller than the safety margin the generator holds
 * every road's gaps to (`levelgen/spawns.ts` → `CROSSING_SAFETY`), so it can
 * make traffic feel unpredictable without eating into the guarantee.
 */
import * as THREE from 'three';
import { VEHICLES, ROADS } from '../config/constants';
import { activeLevel } from '../config/level';
import { Vehicle } from '../entities/Vehicle';
import { vehicleSpawn, isOffMap, type RoadSegment, type WorldSize } from './roads';
import { circleRectOverlap } from './collision';
import { bearingDownOn } from './horn';
import { findJunctions } from './junctions';
import type { Player } from '../entities/Player';
import type { Dog } from '../entities/Dog';
import type { TrafficSpec } from './levelgen/types';

// Spawn/despawn fully off-screen. Sized off the *longest* kind, so a van enters
// and leaves as cleanly as a car rather than popping in with its tail showing.
const MARGIN = VEHICLES.MAX_LENGTH;

/**
 * How much a release may slide off its scheduled time, either way.
 *
 * Small on purpose. It exists so the same street doesn't feel metronomic, and
 * it has to stay well under the slack the generator leaves in every road's
 * crossing window — otherwise the jitter could close a gap the level promised
 * would be there.
 */
export const JITTER_MS = 400;

export class TrafficManager {
  readonly vehicles: Vehicle[] = [];
  private readonly traffic: TrafficSpec;
  private readonly world: WorldSize;
  private elapsedMs = 0;
  /** How far through the schedule we are; wraps with the loop. */
  private next = 0;
  private lap = 0;
  private ended = false;

  /** Fired once when a vehicle touches the player or dog. */
  onHit?: () => void;
  /** Fired when a vehicle is bearing down on the walker or the dog. */
  onHorn?: () => void;

  /** Per-vehicle horn cooldowns (ms remaining), so one car can't drone. */
  private hornCooldowns = new WeakMap<Vehicle, number>();

  constructor(
    private scene: THREE.Scene,
    private player: Player,
    private dog: Dog,
  ) {
    const level = activeLevel();
    this.traffic = level.traffic;
    this.world = { width: level.world.width, height: level.world.height };
  }

  /** Put a vehicle on the road, honouring the level's cap on how many are out. */
  spawnVehicle(release = this.traffic.schedule[this.next % Math.max(1, this.traffic.schedule.length)]): void {
    if (!release) return;
    if (this.vehicles.length >= this.traffic.maxVehicles) return;

    const road = activeLevel().roads[release.road];
    if (!road) return;
    // The **width** has to come along, not just the line. `vehicleSpawn` takes
    // the lane offset off it, so a segment rebuilt without it puts every car on
    // the high street in the gutter and every car on a back street astride the
    // centre line.
    const seg: RoadSegment = { orient: road.orient, pos: road.pos, width: road.width };
    const spawn = vehicleSpawn(
      seg,
      release.dir,
      this.world,
      ROADS.WIDTH,
      this.traffic.speed,
      MARGIN,
    );
    const vehicle = new Vehicle(spawn, release.kind);
    this.scene.add(vehicle.object);
    this.vehicles.push(vehicle);
  }

  update(deltaMs: number, dt: number): void {
    if (this.ended) return;

    this.elapsedMs += deltaMs;
    this.releaseDue();

    const level = activeLevel();
    const junctions = findJunctions(level.roads, ROADS.WIDTH);

    for (let i = this.vehicles.length - 1; i >= 0; i--) {
      const vehicle = this.vehicles[i];
      vehicle.updateVehicle(dt, junctions, level.roads);

      // The vehicle's own footprint against the player/dog circles ends the run.
      const box = vehicle.bounds();
      if (
        circleRectOverlap(this.player.pos, this.player.radius, box) ||
        circleRectOverlap(this.dog.pos, this.dog.radius, box)
      ) {
        this.ended = true;
        this.onHit?.();
        return;
      }

      this.warn(vehicle, deltaMs);

      // Despawn anything that has driven off the map.
      if (isOffMap(vehicle.pos.x, vehicle.pos.y, this.world, MARGIN)) {
        vehicle.dispose(this.scene);
        this.vehicles.splice(i, 1);
      }
    }
  }

  /**
   * Sound the horn if this vehicle is about to run into either of them.
   *
   * The cooldown is ticked for every vehicle whether or not anyone is in front
   * of it, so a car that passes the pair twice — once each way at a junction —
   * doesn't carry a stale timer between the two.
   */
  private warn(vehicle: Vehicle, deltaMs: number): void {
    const cooling = Math.max(0, (this.hornCooldowns.get(vehicle) ?? 0) - deltaMs);
    this.hornCooldowns.set(vehicle, cooling);
    if (cooling > 0) return;

    const threatened =
      bearingDownOn(
        vehicle.pos,
        vehicle.vel,
        this.player.pos,
        VEHICLES.HORN_RADIUS,
        VEHICLES.HORN_CONE,
      ) ||
      bearingDownOn(
        vehicle.pos,
        vehicle.vel,
        this.dog.pos,
        VEHICLES.HORN_RADIUS,
        VEHICLES.HORN_CONE,
      );
    if (!threatened) return;

    this.hornCooldowns.set(vehicle, VEHICLES.HORN_COOLDOWN_MS);
    this.onHorn?.();
  }

  /**
   * Release every vehicle whose time has come.
   *
   * A `while` rather than an `if`: a frame can be up to `MAX_DELTA_MS` long,
   * and on a very busy level that is enough to step past two releases. Dropping
   * the second would quietly make the level easier than the one that was rated.
   * The `maxVehicles` cap in `spawnVehicle` is what stops that turning into a
   * pile-up — a release that can't fit is simply missed, which is the right way
   * round.
   */
  private releaseDue(): void {
    const { schedule, loopMs } = this.traffic;
    if (schedule.length === 0) return;

    for (;;) {
      const release = schedule[this.next];
      const due = release.atMs + this.lap * loopMs + this.jitterFor(this.next, this.lap);
      if (due > this.elapsedMs) return;

      this.spawnVehicle(release);
      this.next++;
      if (this.next >= schedule.length) {
        this.next = 0;
        this.lap++;
      }
    }
  }

  /**
   * A deterministic wobble per release, so the same level plays the same way
   * twice while never sounding like a metronome. Derived from the release's
   * index rather than `Math.random()` for exactly that reason.
   */
  private jitterFor(index: number, lap: number): number {
    const n = Math.sin((index + 1) * 12.9898 + (lap + 1) * 78.233) * 43758.5453;
    return (n - Math.floor(n) - 0.5) * 2 * JITTER_MS;
  }

  /** Halt traffic (called when the run ends). */
  stop(): void {
    this.ended = true;
    for (const vehicle of this.vehicles) vehicle.stop();
  }
}
